// src/utils/modifierUtils.js
/**
 * ModifierUtils.js
 * Utilities for standardizing modifier handling across the application
 */

/**
 * Normalize modifiers from various sources into a consistent format
 * @param {Object} item - Item object that may contain modifiers in different formats
 * @param {Array} directModifiers - Modifiers passed directly (highest priority)
 * @returns {Array} Normalized array of modifier objects with id, name, and price
 */
export const normalizeModifiers = (item, directModifiers = []) => {
  // Priority 1: Use directly provided modifiers
  if (Array.isArray(directModifiers) && directModifiers.length > 0) {
    return directModifiers.map((mod) => ({
      id: mod.id || '',
      name: mod.name || '',
      price: typeof mod.price === 'number' ? mod.price : 0,
    }));
  }

  // Priority 2: Check for modifiers array on the item
  if (Array.isArray(item.modifiers) && item.modifiers.length > 0) {
    return item.modifiers.map((mod) => ({
      id: mod.id || '',
      name: mod.name || '',
      price: typeof mod.price === 'number' ? mod.price : 0,
    }));
  }

  // Priority 3: Check for selectedModifiers object with modifierGroups
  if (
    item.selectedModifiers &&
    Object.keys(item.selectedModifiers).length > 0 &&
    item.originalItem?.modifierGroups
  ) {
    // Instead of importing, extract the modifier data directly
    // This avoids circular dependencies with MenuItemUtils
    const result = [];
    const selectedModifiers = item.selectedModifiers;
    const modifierGroups = item.originalItem.modifierGroups;

    // Process the modifiers directly (same logic as in MenuItemUtils)
    Object.entries(selectedModifiers).forEach(([groupId, selection]) => {
      // Find the modifier group
      const group = modifierGroups.find((g) => g.id === groupId);
      if (!group || !group.modifiers) return;

      // Handle both single selection and multi-selection
      const modifierIds = Array.isArray(selection) ? selection : [selection];

      modifierIds.forEach((modId) => {
        const modifier = group.modifiers.find((m) => m.id === modId);
        if (modifier) {
          result.push({
            id: modifier.id,
            name: modifier.name,
            price: modifier.price || 0,
            groupId,
          });
        }
      });
    });

    return result;
  }

  // Priority 4: Check for modifiers in originalItem
  if (
    item.originalItem &&
    Array.isArray(item.originalItem.modifiers) &&
    item.originalItem.modifiers.length > 0
  ) {
    return item.originalItem.modifiers.map((mod) => ({
      id: mod.id || '',
      name: mod.name || '',
      price: typeof mod.price === 'number' ? mod.price : 0,
    }));
  }

  // Priority 5: Check for modifications array (Clover format)
  if (Array.isArray(item.modifications) && item.modifications.length > 0) {
    return item.modifications.map((mod) => ({
      id: mod.modifier?.id || '',
      name: mod.modifier?.name || '',
      price: mod.amount || 0,
    }));
  }

  // No modifiers found
  return [];
};

/**
 * Format modifiers for Clover API
 */
export const formatModifiersForClover = (modifiers = []) => {
  if (!Array.isArray(modifiers) || modifiers.length === 0) return [];

  return modifiers.map((mod) => ({
    modifier: {
      available: 'true', // String, not boolean
      id: mod.id,
    },
    amount: mod.price || 0,
    name: mod.name,
  }));
};

/**
 * Check if two sets of modifiers are the same
 * Used to determine if an item in the cart is identical to one being added
 * @param {Array} modifiersA - First set of modifiers
 * @param {Array} modifiersB - Second set of modifiers
 * @returns {boolean} True if the modifier sets are identical
 */
export const areModifiersEqual = (modifiersA = [], modifiersB = []) => {
  if (!Array.isArray(modifiersA) || !Array.isArray(modifiersB)) return false;
  if (modifiersA.length !== modifiersB.length) return false;

  // Check that all modifiers from A exist in B
  return modifiersA.every((modA) => {
    return modifiersB.some(
      (modB) => modB.id === modA.id && modB.price === modA.price,
    );
  });
};
